import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, LinkIcon, Copy, Check, Sparkles, Star, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { useConversation, useConversationRaw, useToggleStarred } from '@/hooks/use-conversations';
import { useAllTenants } from '@/hooks/use-tenants';
import { tenantLabel } from '@/lib/cells/cells';
import { buildLogfireTraceUrl, buildGrafanaTraceUrl, extractThreadId, detectLogfireProject, LOGFIRE_PROJECTS } from '@/lib/utils/format';
import { Page, PageContent, PageHeader, PageTitle } from '@/components/common/page';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ConversationView } from '@/components/conversations/ConversationView';
import { toEnvelope } from '@/lib/conversation-envelope';
import { AiAnalysisCard } from '@/components/conversations/ai-analysis-card';

/** Stable reference — an inline array re-runs the viewer's effect. */
const FULL_TRACKS: readonly string[] = ['tool', 'state'];

export const Route = createFileRoute('/agent/conversations/$tenantId/$conversationId')({
  component: ConversationDetailPage,
});

function ConversationDetailPage() {
  const { conversationId, tenantId } = Route.useParams();
  const navigate = Route.useNavigate();
  const [activeTab, setActiveTab] = useState('chat');
  const [copiedJson, setCopiedJson] = useState(false);

  // Fetch conversation data using tenant ID from path
  const { data: conversation, isLoading, error } = useConversation(conversationId, tenantId);
  const { data: rawConversation, isLoading: isLoadingRaw } = useConversationRaw(conversationId, tenantId);
  const { data: tenants } = useAllTenants();
  const toggleStarMutation = useToggleStarred();

  // Get tenant name
  const tenant = tenants?.find(t => t.id === conversation?.tenant_id);
  const tenantName = (tenant && tenantLabel(tenant)) || (conversation?.tenant_id ? conversation.tenant_id.slice(0, 8) : 'Unknown');

  // Get category (prefer manual category over AI category)
  const displayCategory = conversation?.category || conversation?.ai_category || 'Uncategorized';
  const displaySubcategory = conversation?.subcategory || conversation?.ai_subcategory;

  const handleToggleStar = () => {
    if (!conversation) return;
    void toggleStarMutation.mutateAsync({
      conversationId: conversation.id,
      tenantId: tenantId,
    });
  };

  const handleCopyURL = () => {
    const url = `${window.location.origin}/agent/conversations/${tenantId}/${conversationId}`;
    void navigator.clipboard
      .writeText(url)
      .then(() => {
        toast.info('Conversation URL copied to clipboard');
      })
      .catch((e: unknown) => {
        console.log(e);
      });
  };

  const handleCopyJson = () => {
    if (!rawConversation) return;
    void navigator.clipboard
      .writeText(JSON.stringify(rawConversation, null, 2))
      .then(() => {
        setCopiedJson(true);
        setTimeout(() => { setCopiedJson(false); }, 2000);
        toast.success('Raw JSON copied to clipboard');
      })
      .catch((err: unknown) => {
        console.error('Failed to copy:', err);
        toast.error('Failed to copy JSON');
      });
  };

  if (isLoading) {
    return (
      <div className="flex h-full min-w-0 flex-1 items-center justify-center">
        <div className="border-primary h-12 w-12 animate-spin rounded-full border-b-2"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full min-w-0 flex-1 items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-medium text-destructive">Failed to load conversation</div>
          <div className="text-sm text-muted-foreground mt-2">{(error).message}</div>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="flex h-full min-w-0 flex-1 items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-medium text-foreground">Conversation not found</div>
          <div className="text-sm text-muted-foreground mt-2">ID: {conversationId}</div>
        </div>
      </div>
    );
  }

  return (
    <Page>
      <PageHeader>
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                void navigate({ to: '/agent/conversations' });
              }}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <span
                  className="cursor-pointer hover:opacity-70 transition-opacity"
                  onClick={handleToggleStar}
                >
                  <Star
                    className={`h-5 w-5 ${conversation.is_starred ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
                  />
                </span>
                <PageTitle>{conversation.title || conversationId}</PageTitle>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={handleCopyURL} className="h-8 w-8">
                      <LinkIcon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy Conversation URL</TooltipContent>
                </Tooltip>
                {isLoadingRaw ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center justify-center h-8 w-8">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Loading trace data...</TooltipContent>
                  </Tooltip>
                ) : rawConversation?.turns[0] ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <a
                        href={buildLogfireTraceUrl(
                          rawConversation.turns[0].trace_id,
                          detectLogfireProject(rawConversation.agent_version),
                          {
                            since: rawConversation.started_at,
                            until: rawConversation.ended_at,
                          },
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </TooltipTrigger>
                    <TooltipContent>View in Logfire</TooltipContent>
                  </Tooltip>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center justify-center h-8 w-8 text-muted-foreground/40">
                        <ExternalLink className="h-4 w-4" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>No trace data</TooltipContent>
                  </Tooltip>
                )}
                {(() => {
                  const threadId = extractThreadId('source_identifier' in conversation ? conversation.source_identifier : undefined);
                  if (!threadId) return null;
                  return (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <a
                          href={buildGrafanaTraceUrl(threadId, {
                            since: conversation.conversation_date,
                            until: rawConversation?.ended_at,
                          })}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          <ExternalLink className="h-4 w-4 text-orange-500" />
                        </a>
                      </TooltipTrigger>
                      <TooltipContent>View in Grafana</TooltipContent>
                    </Tooltip>
                  );
                })()}
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                <span>Tenant: {tenantName}</span>
                <span>•</span>
                <span>Category: {displayCategory}{displaySubcategory && ` / ${displaySubcategory}`}</span>
                <span>•</span>
                <span>{conversation.turn_count} messages</span>
              </div>
              {conversation.workflow_types && conversation.workflow_types.length > 0 && (
                <div className="flex gap-1 mt-2">
                  {conversation.workflow_types.map(wf => (
                    <span key={wf} className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                      {wf.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </PageHeader>
      <PageContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-1 flex-col overflow-hidden h-full">
          <div className="flex flex-row items-center gap-2">
            <TabsList>
              <TabsTrigger value="chat">Chat ({conversation.turn_count} messages)</TabsTrigger>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="raw">Raw JSON</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="chat" className="flex-1 overflow-y-auto pt-4">
            <ConversationView
              data={toEnvelope(conversation, rawConversation)}
              show={FULL_TRACKS}
            />
          </TabsContent>

          <TabsContent value="details" className="flex-1 overflow-y-auto pt-4">
            <div className="space-y-4 max-w-4xl">
              {/* Categorization Section */}
              <div className="rounded-lg border bg-card p-4">
                <h4 className="font-semibold mb-3">Categorization</h4>
                <div className="grid grid-cols-2 gap-4">
                  {/* AI Categories */}
                  <div>
                    <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-2">
                      <Sparkles className="w-3 h-3 text-purple-500" />
                      AI Generated
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="font-medium">Category:</span>{' '}
                        <span className="text-purple-700">{conversation.ai_category || 'None'}</span>
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">Subcategory:</span>{' '}
                        <span className="text-purple-600">{conversation.ai_subcategory || 'None'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Manual Categories */}
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-2">Manual Annotation</div>
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="font-medium">Category:</span>{' '}
                        <span className="text-gray-900">{conversation.category || 'None'}</span>
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">Subcategory:</span>{' '}
                        <span className="text-gray-700">{conversation.subcategory || 'None'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <AiAnalysisCard
                sentiment={conversation.sentiment}
                aiSentiment={conversation.ai_sentiment}
                categoryReasoning={conversation.category_reasoning}
                interestLevel={conversation.interest_level}
                interestLevelReasoning={conversation.interest_level_reasoning}
                resolution={conversation.resolution}
                resolutionReasoning={conversation.resolution_reasoning}
              />

              {/* Metadata Section */}
              <div className="rounded-lg border bg-card p-4">
                <h4 className="font-semibold mb-3">Conversation Details</h4>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">ID:</dt>
                    <dd className="font-mono">{conversation.id}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Tenant ID:</dt>
                    <dd className="font-mono">{conversation.tenant_id}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Date:</dt>
                    <dd>{new Date(conversation.conversation_date).toLocaleString()}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Messages:</dt>
                    <dd>{conversation.turn_count}</dd>
                  </div>
                  {conversation.initiated_by_employee_name && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Initiated By:</dt>
                      <dd>{conversation.initiated_by_employee_name}</dd>
                    </div>
                  )}
                  {conversation.app_name && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">App Name:</dt>
                      <dd>
                        <span className="inline-block px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs">
                          {conversation.app_name}
                        </span>
                      </dd>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <dt className="text-muted-foreground">Starred:</dt>
                    <dd>
                      <span
                        className="cursor-pointer hover:opacity-70 transition-opacity inline-flex"
                        onClick={handleToggleStar}
                      >
                        <Star
                          className={`h-4 w-4 ${conversation.is_starred ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
                        />
                      </span>
                    </dd>
                  </div>
                  {'source' in conversation && conversation.source && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Source:</dt>
                      <dd className="font-semibold capitalize">{conversation.source}</dd>
                    </div>
                  )}
                  {'source_identifier' in conversation && conversation.source_identifier && (() => {
                    const sourceId = conversation.source_identifier;
                    return (
                      <div className="flex justify-between items-center gap-2">
                        <dt className="text-muted-foreground">Source ID:</dt>
                        <dd className="flex items-center gap-2">
                          <span className="font-mono text-xs" title={sourceId}>
                            {sourceId}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              void navigator.clipboard.writeText(sourceId).then(() => {
                                toast.success('Source ID copied to clipboard');
                              });
                            }}
                            className="p-1 hover:bg-muted rounded transition-colors"
                          >
                            <Copy className="h-3 w-3 text-muted-foreground" />
                          </button>
                        </dd>
                      </div>
                    );
                  })()}
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">S3 Location:</dt>
                    <dd className="font-mono text-xs">{conversation.s3_key}</dd>
                  </div>
                </dl>
              </div>

              {/* Logfire Traces Section */}
              {rawConversation?.turns && rawConversation.turns.length > 0 && (() => {
                const detectedProject = detectLogfireProject(rawConversation.agent_version);
                const otherKeys = (Object.keys(LOGFIRE_PROJECTS) as (keyof typeof LOGFIRE_PROJECTS)[]).filter(k => k !== detectedProject);
                return (
                  <div className="rounded-lg border bg-card p-4">
                    <h4 className="font-semibold mb-3">Logfire Traces</h4>
                    <div className="space-y-3">
                      {rawConversation.turns.map((turn) => (
                        <div key={turn.trace_id} className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground shrink-0">
                              Turn {turn.turn_number}
                            </span>
                            <span className="font-mono text-xs text-muted-foreground truncate" title={turn.trace_id}>
                              {turn.trace_id}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                void navigator.clipboard.writeText(turn.trace_id).then(() => {
                                  toast.info('Trace ID copied to clipboard');
                                });
                              }}
                              className="p-1 hover:bg-muted rounded transition-colors shrink-0"
                            >
                              <Copy className="h-3 w-3 text-muted-foreground" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 ml-1">
                            <a
                              href={buildLogfireTraceUrl(turn.trace_id, detectedProject, {
                                since: turn.timestamp,
                                until: turn.agent_responses.at(-1)?.end_timestamp,
                              })}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium"
                            >
                              <ExternalLink className="h-3 w-3" />
                              {/* eslint-disable-next-line security/detect-object-injection */}
                              {LOGFIRE_PROJECTS[detectedProject]}
                            </a>
                            {otherKeys.map((key) => (
                              <a
                                key={key}
                                href={buildLogfireTraceUrl(turn.trace_id, key, {
                                  since: turn.timestamp,
                                  until: turn.agent_responses.at(-1)?.end_timestamp,
                                })}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
                              >
                                <ExternalLink className="h-3 w-3" />
                                {/* eslint-disable-next-line security/detect-object-injection */}
                                {LOGFIRE_PROJECTS[key]}
                              </a>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </TabsContent>

          <TabsContent value="raw" className="flex-1 overflow-y-auto pt-4">
            <div className="rounded-lg border bg-card p-4 relative">
              <Button
                variant="outline"
                size="icon"
                className="absolute top-4 right-4 h-8 w-8"
                onClick={handleCopyJson}
              >
                {copiedJson ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
              <pre className="text-xs overflow-auto pr-24">
                {rawConversation ? JSON.stringify(rawConversation, null, 2) : 'Loading raw JSON...'}
              </pre>
            </div>
          </TabsContent>
        </Tabs>
      </PageContent>
    </Page>
  )
}
